import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import http from "../../services/http";

/* ─── CSV export helper ─── */
const downloadCSV = (filename, rows, headers) => {
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [
    headers.map(escape).join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const statusBadge = (s = "") => {
  const map = {
    live:      { bg: "#dcfce7", color: "#16a34a" },
    completed: { bg: "#e0f2fe", color: "#0369a1" },
    upcoming:  { bg: "#fef9c3", color: "#92400e" },
  };
  const m = map[s.toLowerCase()] || { bg: "#f1f5f9", color: "#64748b" };
  return (
    <span style={{ ...ts.badge, background: m.bg, color: m.color }}>
      {s || "—"}
    </span>
  );
};

export const MatchBetsReportLayer = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab]         = useState("section_a_match");   // section_a_match | section_a_questions | section_c

  /* filters */
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [search,   setSearch]   = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      const res = await http.get("reports_match_bets", { params });
      if (res.data?.status) {
        setData(res.data);
      } else {
        toast.error(res.data?.msg || "Failed to load report");
      }
    } catch {
      toast.error("Server error loading report");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── filtered rows ── */
  const rows = useMemo(() => {
    if (!data) return [];
    let source = [];
    if (tab === "section_a_match")     source = data.section_a_match_bets  || [];
    if (tab === "section_a_questions") source = data.section_a_questions    || [];
    if (tab === "section_c")           source = data.section_c              || [];
    if (!search.trim()) return source;
    const v = search.toLowerCase();
    return source.filter((r) =>
      (r.match_name || "").toLowerCase().includes(v) ||
      (r.question_title || "").toLowerCase().includes(v)
    );
  }, [data, tab, search]);

  /* ── export ── */
  const handleExport = () => {
    if (!rows.length) { toast.warn("Nothing to export"); return; }
    const baseHeaders = ["match_id", "match_name", "match_status", "date_time",
      "total_bets", "total_amount", "unique_bettors", "total_won", "total_lost",
      "total_pending", "won_count", "lost_count", "pending_count"];
    const headers = (tab === "section_a_questions" || tab === "section_c")
      ? ["question_id", "question_title", ...baseHeaders]
      : baseHeaders;
    const sectionLabel = tab === "section_a_match" ? "SectionA_MatchBets"
      : tab === "section_a_questions" ? "SectionA_QuestionBets"
      : "SectionC_SelectionBets";
    downloadCSV(`${sectionLabel}_Report_${new Date().toISOString().slice(0,10)}.csv`, rows, headers);
    toast.success("CSV exported");
  };

  const totals = data?.totals || {};

  return (
    <div style={s.root}>
      {/* ── Header ── */}
      <div style={s.headerCard}>
        <div style={s.headerLeft}>
          <div style={s.iconWrap}>
            <Icon icon="solar:chart-square-bold-duotone" style={{ fontSize: 24 }} />
          </div>
          <div>
            <h4 style={s.pageTitle}>Match Bets Report</h4>
            <p style={s.pageSub}>Section A & C — bet statistics per match &amp; question</p>
          </div>
        </div>
        <div style={s.headerRight}>
          <button style={s.exportBtn} onClick={handleExport} disabled={loading}>
            <Icon icon="solar:file-download-bold" style={{ fontSize: 16 }} />
            Export CSV
          </button>
          <button style={s.refreshBtn} onClick={fetchData} disabled={loading}>
            <Icon icon={loading ? "svg-spinners:ring-resize" : "solar:refresh-bold"} style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={s.filterRow}>
        <div style={s.filterGroup}>
          <label style={s.label}>From Date</label>
          <input type="date" style={s.input} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div style={s.filterGroup}>
          <label style={s.label}>To Date</label>
          <input type="date" style={s.input} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div style={{ ...s.filterGroup, flex: 1 }}>
          <label style={s.label}>Search</label>
          <div style={{ position: "relative" }}>
            <Icon icon="ion:search-outline" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 15, color: "#94a3b8", pointerEvents: "none" }} />
            <input style={{ ...s.input, paddingLeft: 32 }} placeholder="Match name or question…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div style={s.summaryGrid}>
        {[
          { label: "Section A — Total Bets",    value: (totals.section_a?.total_bets    || 0).toLocaleString(), icon: "solar:checklist-bold-duotone",        color: "#3b82f6" },
          { label: "Section A — Total Invested", value: fmt(totals.section_a?.total_amount || 0),                icon: "solar:wallet-bold-duotone",            color: "#8b5cf6" },
          { label: "Section A — Total Won",      value: fmt(totals.section_a?.total_won    || 0),                icon: "solar:cup-bold-duotone",               color: "#16a34a" },
          { label: "Section C — Total Bets",    value: (totals.section_c?.total_bets    || 0).toLocaleString(), icon: "solar:question-circle-bold-duotone",   color: "#f59e0b" },
          { label: "Section C — Total Invested", value: fmt(totals.section_c?.total_amount || 0),                icon: "solar:wallet-money-bold-duotone",       color: "#e05252" },
          { label: "Section C — Total Won",      value: fmt(totals.section_c?.total_won    || 0),                icon: "solar:medal-ribbons-star-bold-duotone", color: "#0ea5e9" },
        ].map((c) => (
          <div key={c.label} style={s.sumCard}>
            <div style={{ ...s.sumIcon, background: `${c.color}18`, color: c.color }}>
              <Icon icon={c.icon} style={{ fontSize: 22 }} />
            </div>
            <div>
              <div style={s.sumValue}>{c.value}</div>
              <div style={s.sumLabel}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div style={s.tabRow}>
        {[
          { key: "section_a_match",     label: "Section A — Match Bets",     icon: "solar:checklist-bold-duotone" },
          { key: "section_a_questions", label: "Section A — Question Bets",   icon: "solar:question-square-bold-duotone" },
          { key: "section_c",           label: "Section C — Selection Bets",  icon: "solar:gamepad-bold-duotone" },
        ].map((t) => (
          <button key={t.key} style={{ ...s.tabBtn, ...(tab === t.key ? s.tabActive : {}) }} onClick={() => setTab(t.key)}>
            <Icon icon={t.icon} style={{ fontSize: 14, marginRight: 6 }} />
            {t.label}
            <span style={{ ...s.tabCount, background: tab === t.key ? "rgba(255,255,255,0.25)" : "#f1f5f9" }}>
              {tab === t.key ? rows.length : (
                t.key === "section_a_match"     ? (data?.section_a_match_bets?.length || 0) :
                t.key === "section_a_questions" ? (data?.section_a_questions?.length  || 0) :
                                                   (data?.section_c?.length           || 0)
              )}
            </span>
          </button>
        ))}
      </div>

      {/* ── Table ── */}
      <div style={s.card}>
        {loading ? (
          <div style={s.emptyCell}>
            <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} />
            Loading report…
          </div>
        ) : rows.length === 0 ? (
          <div style={s.emptyCell}>
            <Icon icon="solar:chart-2-bold-duotone" style={{ fontSize: 40, color: "#cbd5e1", display: "block", margin: "0 auto 10px" }} />
            No data available for the selected filters
          </div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {(tab === "section_a_questions" || tab === "section_c") && (
                    <th style={s.th}>Question</th>
                  )}
                  <th style={s.th}>Match</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Date</th>
                  <th style={{ ...s.th, textAlign: "right" }}>Bets</th>
                  <th style={{ ...s.th, textAlign: "right" }}>Total Invested</th>
                  <th style={{ ...s.th, textAlign: "right" }}>Total Won</th>
                  <th style={{ ...s.th, textAlign: "right" }}>Total Lost</th>
                  <th style={{ ...s.th, textAlign: "right" }}>Pending</th>
                  <th style={{ ...s.th, textAlign: "right" }}>Unique Users</th>
                  <th style={{ ...s.th, textAlign: "right" }}>W / L / P</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}
                  >
                    {(tab === "section_a_questions" || tab === "section_c") && (
                      <td style={s.td}>
                        <span style={{ fontWeight: 600, color: "#1e293b", fontSize: "0.82rem" }}>
                          {row.question_title || `Q#${row.question_id}`}
                        </span>
                        {row.timing_type && (
                          <div style={{ fontSize: "0.7rem", color: "#94a3b8", marginTop: 2 }}>{row.timing_type}</div>
                        )}
                      </td>
                    )}
                    <td style={s.td}>
                      <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "0.83rem" }}>{row.match_name || "—"}</div>
                    </td>
                    <td style={s.td}>{statusBadge(row.match_status)}</td>
                    <td style={s.td}><span style={{ color: "#64748b", fontSize: "0.79rem" }}>{row.date_time || "—"}</span></td>
                    <td style={{ ...s.td, textAlign: "right" }}>
                      <span style={{ fontWeight: 700, color: "#1e293b" }}>{(row.total_bets || 0).toLocaleString()}</span>
                    </td>
                    <td style={{ ...s.td, textAlign: "right" }}>
                      <span style={{ fontWeight: 700, color: "#8b5cf6" }}>{fmt(row.total_amount)}</span>
                    </td>
                    <td style={{ ...s.td, textAlign: "right" }}>
                      <span style={{ fontWeight: 700, color: "#16a34a" }}>{fmt(row.total_won)}</span>
                    </td>
                    <td style={{ ...s.td, textAlign: "right" }}>
                      <span style={{ fontWeight: 700, color: "#dc2626" }}>{fmt(row.total_lost)}</span>
                    </td>
                    <td style={{ ...s.td, textAlign: "right" }}>
                      <span style={{ color: "#f59e0b", fontWeight: 600 }}>{fmt(row.total_pending || 0)}</span>
                    </td>
                    <td style={{ ...s.td, textAlign: "right" }}>
                      <span style={{ fontWeight: 600, color: "#475569" }}>{row.unique_bettors || 0}</span>
                    </td>
                    <td style={{ ...s.td, textAlign: "right" }}>
                      <span style={{ color: "#16a34a", fontWeight: 700 }}>{row.won_count || 0}</span>
                      <span style={{ color: "#94a3b8", margin: "0 3px" }}>/</span>
                      <span style={{ color: "#dc2626", fontWeight: 700 }}>{row.lost_count || 0}</span>
                      <span style={{ color: "#94a3b8", margin: "0 3px" }}>/</span>
                      <span style={{ color: "#f59e0b", fontWeight: 700 }}>{row.pending_count || 0}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },

  headerCard: {
    background: "#fff", borderRadius: 16, padding: "18px 24px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    marginBottom: 20, flexWrap: "wrap", gap: 12,
  },
  headerLeft:  { display: "flex", alignItems: "center", gap: 14 },
  headerRight: { display: "flex", gap: 8 },
  iconWrap: {
    width: 48, height: 48, borderRadius: 12,
    background: "linear-gradient(135deg,#16632b,#1da144)",
    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  pageTitle: { margin: 0, fontWeight: 800, fontSize: "1.35rem", color: "#0f172a" },
  pageSub:   { margin: "3px 0 0", fontSize: "0.82rem", color: "#64748b" },
  exportBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "8px 16px", background: "linear-gradient(135deg,#16632b,#1da144)",
    border: "none", borderRadius: 9, color: "#fff",
    fontSize: "0.82rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
  },
  refreshBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 36, height: 36, border: "1.5px solid #e2e8f0",
    background: "#fff", borderRadius: 9, cursor: "pointer", color: "#475569",
  },

  filterRow: {
    background: "#fff", borderRadius: 14, padding: "16px 20px",
    display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap",
    border: "1px solid #e8edf2", marginBottom: 20,
  },
  filterGroup: { display: "flex", flexDirection: "column", minWidth: 140 },
  label: { fontSize: "0.72rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: {
    padding: "8px 12px", border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.84rem", color: "#1e293b", background: "#f8fafc",
    outline: "none", fontFamily: "inherit",
  },

  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 20 },
  sumCard: {
    background: "#fff", borderRadius: 14, padding: "14px 16px",
    display: "flex", alignItems: "center", gap: 12,
    border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  sumIcon:  { width: 44, height: 44, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  sumValue: { fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  sumLabel: { fontSize: "0.68rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 3 },

  tabRow: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  tabBtn: {
    display: "flex", alignItems: "center",
    padding: "9px 16px", border: "1.5px solid #e2e8f0",
    background: "#fff", borderRadius: 10, cursor: "pointer",
    fontSize: "0.83rem", fontWeight: 600, color: "#475569",
    fontFamily: "inherit", transition: "all 0.15s",
  },
  tabActive: {
    background: "linear-gradient(135deg,#0f4c25,#16632b)",
    color: "#fff", border: "none",
    boxShadow: "0 2px 8px rgba(22,99,43,0.25)",
  },
  tabCount: {
    marginLeft: 8, padding: "2px 8px", borderRadius: 100,
    fontSize: "0.7rem", fontWeight: 700,
  },

  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.81rem" },
  th: {
    padding: "10px 14px", background: "#f8fafc", color: "#64748b",
    fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase",
    letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9",
    whiteSpace: "nowrap",
  },
  td: { padding: "10px 14px", borderBottom: "1px solid #f8fafc", color: "#334155", verticalAlign: "middle" },
  emptyCell: { padding: "60px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },
};

const ts = {
  badge: {
    display: "inline-flex", alignItems: "center",
    padding: "3px 9px", borderRadius: 100,
    fontSize: "0.7rem", fontWeight: 700, textTransform: "capitalize",
  },
};
